import {
    IMAGE_CONFIG,
    Location,
} from "@angular/common";
import {
    HttpClient,
    provideHttpClient,
    withFetch,
    withInterceptors,
} from "@angular/common/http";
import {
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    inject,
    provideAppInitializer,
} from "@angular/core";
import {
    provideClientHydration,
    withEventReplay,
    withHttpTransferCacheOptions,
    withIncrementalHydration,
} from "@angular/platform-browser";
import {
    provideRouter,
    Router,
    withComponentInputBinding,
    withDisabledInitialNavigation,
    withInMemoryScrolling,
} from "@angular/router";
import {
    cacheBypassInterceptor,
    checkAccessInterceptor,
    getTokenInterceptor,
    setLocalInterceptor,
    setTimezoneInterceptor,
    setTokenInterceptor,
} from "@Front-end/seed/interceptors";
import { Domain } from "@Front-end/seed/models/common";
import { provideSeedIcon } from "@Front-end/seed/modules/icons";
import { provideSeedPromoCode } from "@Front-end/seed/modules/promocode";
import { provideSeedProviders } from "@Front-end/seed/modules/providers";
import {
    defaultCountryV2Interceptor,
    provideSeedRegistrationV2,
} from "@Front-end/seed/modules/registration-v2";
import { provideSeedShop } from "@Front-end/seed/modules/shop";
import { provideSeedTagManager } from "@Front-end/seed/modules/tag-manager";
import {
    provideSeedUser,
    provideUrlTokenInitializer,
} from "@Front-end/seed/modules/user";
import { provideSeedUserBonuses } from "@Front-end/seed/modules/user-bonuses";
import { provideSeedWheel } from "@Front-end/seed/modules/v2/lucky-wheel";
import { provideSeedMissions } from "@Front-end/seed/modules/v2/missions";
import { provideSeedVerifyEmail } from "@Front-end/seed/modules/verify-email";
import { withSeedConfigurationProvider } from "@Front-end/seed/providers";
import { REST_LOCALE } from "@Front-end/seed/seed-config";
import { provideAutoRefreshOnActivate } from "@Front-end/seed/services/page-activate";
import { provideVipContent } from "@Front-end/seed/services/vip-content";
import { withIconProvider } from "@Front-end/ui/modules/icon";
import { withModalProvider } from "@Front-end/ui/modules/modal";
import { withNotificationProvider } from "@Front-end/ui/modules/notifications";
import {
    LocalizeParser,
    LocalizeRouterSettings,
    ManualParserLoader,
    withLocalizeRouter,
} from "@gilsdav/ngx-translate-router";
import { provideEffects } from "@ngrx/effects";
import { provideStore } from "@ngrx/store";
import {
    TranslateLoader,
    TranslateModule,
    TranslateService,
} from "@ngx-translate/core";
import * as Sentry from "@sentry/angular";
import { BehaviorSubject } from "rxjs";
import { environment } from "../environments/environment";
import { CustomTranslateLoaderService } from "./custom-translate-loader.service";
import { errorNotificationInterceptor } from "./interceptors/error-notification.interceptor";
import { localizeErrorInterceptor } from "./interceptors/localize-error.interceptor";
import { mirrorUrlResolverInterceptor } from "./interceptors/mirror-url-resolver.interceptor";
import { sessionExpiredInterceptor } from "./interceptors/session-expired.interceptor";
import { sportbookGamesInterceptor } from "./interceptors/sportbook/sportbook-games.interceptor";
import { sportbookProviderInterceptor } from "./interceptors/sportbook/sportbook-provider.interceptor";
import { ssrHttpTimeoutInterceptor } from "./interceptors/ssr-http-timeout.interceptor";
import { tournamentGamesInterceptor } from "./interceptors/tournament-game/tournament-games.interceptor";
import { routes } from "./modules/main/main-routing";
import { provideSsrRequestLogger } from "./provideSsrRequestLogger";
import { AntifraudService } from "./services/antifraud.service";
import { LocalizeService } from "./services/localize.service";
import { WebSocketInitService } from "./services/websocket-init.service";
import { getStoreDevtoolsProviders } from "./store-devtools.providers";
import { AppPopupsEffects } from "./store/app-popups.effects";
import { AppUserEffects } from "./store/app-user.effects";

const SPRITE_VERSION: string = "1";

export const appConfig: ApplicationConfig = {
    providers: [
        ...(environment.domain !== "PROD" ? [provideSsrRequestLogger()] : []),
        {
            provide: IMAGE_CONFIG,
            useValue: {
                disableImageSizeWarning: true,
                disableImageLazyLoadWarning: true,
            },
        },
        importProvidersFrom([
            TranslateModule.forRoot(
                {
                    loader: {
                        provide: TranslateLoader,
                        useClass: CustomTranslateLoaderService,
                        deps: [HttpClient],
                    },
                },
            ),
        ]),
        withModalProvider(),
        withNotificationProvider({ autoClose: 5000 }),
        withIconProvider({ path: `assets/output/sprite.svg?v=${SPRITE_VERSION}` }),
        withSeedConfigurationProvider({
            restConfig: {
                production: environment.production,
                publicGateway: environment.host,
                publicGatewayV2: environment.portalUrl,
                locale: {
                    provide: REST_LOCALE,
                    useFactory(localizeService: LocalizeService): BehaviorSubject<string> {
                        return localizeService.language$;
                    },
                    deps: [LocalizeService],
                },
            },
            webSocketConfig: {
                publicGateway: environment.wsHost,
                publicGatewayV2: environment.wsHostV2,
                reconnectAttempts: Number(environment.wsReconnectAttempts),
                reconnectInterval: Number(environment.wsReconnectInterval),
            },
            notificationsConfig: {
                blockList: ["403", "2", "UNKNOWN_ERROR", "4", "14", "2000121", "2000002", "500"],
            },
            cookieConfig: {
                expireCookieDays: environment.expireCookieDays,
            },
            domain: environment.domain as Domain,
            projectName: environment.uniqueProjectTranslation.projectName,
            features: {
                kycAidVerification: environment.features.kycAidVerification,
                cashierLimits: environment.features.cashierLimits,
                limits: environment.features.limits,
                promoCodeV2: environment.features.promoCodeV2,
                antifraud: environment.features?.antifraud,
            },
            recaptchaConfig: {
                siteKey: environment.recaptchaSiteKey,
                enabled: environment.features.recaptcha,
            },
        }),
        provideSeedRegistrationV2(),
        // remove if shop banner on main will change
        provideSeedShop(),
        provideAutoRefreshOnActivate(),
        provideSeedPromoCode(),
        provideSeedUser(),
        provideSeedWheel(),
        provideSeedTagManager(),
        provideSeedUserBonuses(),
        provideSeedProviders(),
        provideSeedMissions(),
        provideUrlTokenInitializer(),
        provideSeedVerifyEmail(),
        provideSeedIcon(),
        provideVipContent(),
        provideHttpClient(
            withFetch(),
            withInterceptors([
                cacheBypassInterceptor,
                setTokenInterceptor,
                getTokenInterceptor,
                checkAccessInterceptor,
                sessionExpiredInterceptor,
                errorNotificationInterceptor,
                localizeErrorInterceptor,
                sportbookProviderInterceptor,
                sportbookGamesInterceptor,
                tournamentGamesInterceptor,
                defaultCountryV2Interceptor,
                setLocalInterceptor,
                mirrorUrlResolverInterceptor,
                setTimezoneInterceptor,
                ssrHttpTimeoutInterceptor,
            ]),
        ),
        provideRouter(
            routes,
            withDisabledInitialNavigation(),
            withInMemoryScrolling({
                scrollPositionRestoration: "top",
                anchorScrolling: "enabled",
            }),
            withLocalizeRouter(routes, {
                parser: {
                    provide: LocalizeParser,
                    useFactory: (createTranslateLoader),
                    deps: [TranslateService, Location, LocalizeRouterSettings],
                },
                initialNavigation: true,
                useCachedLang: false,
            }),
            withComponentInputBinding(),
        ),
        provideClientHydration(
            withEventReplay(),
            withIncrementalHydration(),
            withHttpTransferCacheOptions({
                includePostRequests: true,
                includeHeaders: [
                    "x-country",
                    "x-current-page",
                    "x-total-pages",
                    "x-total-records",
                ],
            }),
        ),
        provideStore({}),
        ...getStoreDevtoolsProviders(),
        provideEffects([
            AppPopupsEffects,
            AppUserEffects,
        ]),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        }),
        provideAppInitializer(() => {
            inject(WebSocketInitService).init();
        }),
        provideAppInitializer(() => {
            inject(AntifraudService).init();
        }),
    ],
};

export function createTranslateLoader(
    translate: TranslateService,
    location: Location,
    settings: LocalizeRouterSettings,
): ManualParserLoader {
    return new ManualParserLoader(
        translate,
        location,
        settings,
        environment.local,
        "ROUTES.",
    );
}
